import {
  Card,
  Box,
  Typography,
  CardActionArea,
  IconButton,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import type { Theme } from '@mui/material/styles'
import type { SystemStyleObject } from '@mui/system'

import texts from '../../texts'
import fallbackImg from '../../../assets/images/default_fallback_image.png'
import styles from './articleCard.styles'
import { useIsDesktop } from '../../../utils'
import { formatLongDate } from '../../../format'
import { Article } from '../../../types/domain'

const MAX_MS_FOR_HOUR_DATE_FORMAT = 1000 * 60 * 60 * 48 // 2 days

const formatDate = (dateString: string): string => {
  const timeDiff = new Date().getTime() - new Date(dateString).getTime()

  if (timeDiff < MAX_MS_FOR_HOUR_DATE_FORMAT) {
    const hourDiff = Math.floor(timeDiff / (1000 * 60 * 60))
    return `${texts.dateSince()}${hourDiff}${
      hourDiff === 1 ? texts.hour() : texts.hours()
    }`
  }

  return `${texts.dateOn()}${formatLongDate(dateString)}`
}

export enum ARTICLE_DATE_TYPE {
  READ = 'read',
  PUBLISHED = 'published',
}

const ArticleCardContent = ({
  id,
  data,
  onClickDelete,
  dateType = ARTICLE_DATE_TYPE.PUBLISHED,
}: Pick<ArticleCardProps, 'id' | 'data' | 'onClickDelete' | 'dateType'>) => {
  const isDesktop = useIsDesktop()

  const dateLabel =
    dateType === ARTICLE_DATE_TYPE.PUBLISHED
      ? texts.publishedDate()
      : texts.readDate()

  const mediaImage = data?.mediaIconUrl ?? data?.mediaImageUrl

  return (
    <Box
      id={`article-card-content-${id}`}
      sx={{ ...styles.flexColumn, justifyContent: 'space-between' }}
    >
      <Box sx={{ ...styles.flexColumn, justifyContent: 'flex-start' }}>
        {data?.category && (
          <Typography variant='subtitle2' sx={styles.category}>
            {data.category.toUpperCase()}
          </Typography>
        )}

        <Typography variant='cardHeading' sx={styles.clampedTitle}>
          {data?.title}
        </Typography>
      </Box>
      <Box sx={styles.flexRow}>
        <Box sx={styles.metaInfo}>
          {Boolean(mediaImage) && (
            <Box sx={styles.mediaImage}>
              <img
                src={mediaImage}
                alt={`logo del medio ${data?.mediaName ?? ''}`}
              />
            </Box>
          )}
          {data?.mediaIconUrl && <Typography>{data.mediaName}</Typography>}
          {Boolean(data?.date) && data?.mediaIconUrl && (
            <div className='separator'></div>
          )}
          {Boolean(data?.date) && (
            <Typography className='light'>
              {isDesktop && dateLabel} {formatDate(data.date as string)}
            </Typography>
          )}
        </Box>
        {!!onClickDelete && (
          <IconButton
            aria-label={texts.getDeleteAction()}
            onClick={() => onClickDelete(data)}
            sx={styles.deleteButton}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  )
}

interface ArticleCardProps {
  id?: string
  data: Article
  onClick: (url: string) => void
  onClickDelete?: (article: Article) => void
  variant?: 'landscape' | 'portrait'
  dateType?: ARTICLE_DATE_TYPE
  withoutImage?: boolean
  cardActionDisabled?: boolean
  sx?: SystemStyleObject<Theme>
}

export const ArticleCard = ({
  id,
  data,
  onClick,
  onClickDelete,
  variant = 'landscape',
  dateType = ARTICLE_DATE_TYPE.PUBLISHED,
  withoutImage = false,
  cardActionDisabled = false,
  sx = {},
}: ArticleCardProps) => {
  const CardRootComponent: React.ElementType = cardActionDisabled
    ? Box
    : CardActionArea

  const image = !data?.image || data.image === '' ? fallbackImg : data.image
  const variantSx =
    variant === 'landscape' ? styles.rootLandscape : styles.rootPortrait

  return (
    <CardRootComponent
      id={id}
      onClick={() => onClick(data?.url)}
      disableTouchRipple={cardActionDisabled}
      sx={[styles.root, variantSx, sx]}
    >
      <Card sx={styles.card}>
        {variant === 'portrait' && !withoutImage && (
          <Box
            component='img'
            sx={styles.imagePortrait}
            src={image}
            alt='Imagen del artículo'
          />
        )}

        <Box sx={styles.cardInner}>
          {variant === 'landscape' && !withoutImage && (
            <Box
              component='img'
              sx={styles.imageLandscape}
              src={image}
              alt='Imagen del artículo'
            />
          )}
          <ArticleCardContent
            id={id}
            data={data}
            onClickDelete={onClickDelete}
            dateType={dateType}
          />
        </Box>
      </Card>
    </CardRootComponent>
  )
}
